import { useEffect, useState } from "react";
import { Github, Check, X, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function GitHubConnect() {
  const [state, setState] = useState({ loading: true, connected: false, account: null });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setState(s => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/github/me");
      setState({ loading: false, connected: !!data.connected, account: data.account });
    } catch {
      setState({ loading: false, connected: false, account: null });
    }
  };

  useEffect(() => { refresh(); }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/github/oauth/start");
      window.location.href = data.authorize_url;
    } catch {
      toast.error("Could not start GitHub OAuth");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect GitHub from your Durga account?")) return;
    setBusy(true);
    try {
      await api.post("/github/disconnect");
      toast.success("GitHub disconnected");
      await refresh();
    } catch {
      toast.error("Could not disconnect");
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <div className="card-soft p-5 inline-flex items-center gap-2 text-ink2 text-sm" data-testid="github-connect-loading">
        <RefreshCw className="w-4 h-4 animate-spin" /> Checking GitHub…
      </div>
    );
  }

  if (state.connected) {
    const a = state.account || {};
    return (
      <div className="card-soft p-5 flex items-center gap-4" data-testid="github-connect-connected">
        {a.avatar_url ? (
          <img src={a.avatar_url} alt={a.login} className="w-10 h-10 rounded-full border border-border" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-paper border border-border flex items-center justify-center">
            <Github className="w-5 h-5 text-ink2" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm text-ink2">GitHub connected</div>
          <div className="font-semibold text-ink truncate">@{a.login}</div>
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-teal/15 text-teal text-xs font-bold uppercase tracking-wider">
          <Check className="w-3 h-3" /> Linked
        </span>
        <button
          onClick={disconnect}
          disabled={busy}
          className="text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-ink2 hover:border-ink hover:text-ink transition disabled:opacity-40"
          data-testid="github-disconnect-button"
        >
          <X className="w-3.5 h-3.5" /> Disconnect
        </button>
      </div>
    );
  }

  return (
    <div className="card-soft p-5 flex items-center gap-4" data-testid="github-connect-disconnected">
      <div className="w-10 h-10 rounded-full bg-ink text-cream flex items-center justify-center">
        <Github className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-ink">Connect GitHub</div>
        <div className="text-sm text-ink2">Push starter kits to a repo or convert any web/mobile app into an Apple app.</div>
      </div>
      <button
        onClick={connect}
        disabled={busy}
        className="btn-primary text-sm py-2"
        data-testid="github-connect-button"
      >
        <Github className="w-4 h-4" /> {busy ? "Redirecting…" : "Connect GitHub"}
      </button>
    </div>
  );
}
